/**
 * Witch (Bedrock numbers): 26 HP, speed 0.25, follows the player within 16
 * blocks and lobs splash potions. Potion choice (vanilla Witch.performRangedAttack):
 *   distance >= 8 and target not slowed -> slowness
 *   else target health >= 8 and not poisoned -> poison
 *   else distance <= 3 and target not weak (25%) -> weakness
 *   else harming
 * Drinking (vanilla Witch.aiStep, checked while not already drinking):
 *   in water without water breathing (100%), on fire without fire resistance (15%),
 *   hurt (5%) -> healing, 50% while the target is > 10 away without speed -> swiftness.
 * Drops: glass bottle, glowstone dust, gunpowder, redstone, spider eye, sugar,
 * sticks, each 0-2. Spawns in swamps in the dark and (hook) in swamp huts.
 *
 * `witchHooks` is wired in main.ts: it owns the potion simulation and the
 * player's effect set.
 */

import { I } from '../../game/itemIds';
import { potionByKey } from '../../game/brewing/potions';
import { FX, giveMobEffect } from '../../game/effects/effects';
import { stackOf, type Stack } from '../../game/types';
import { Hostile } from '../hostile';
import type { MobCtx } from '../mob';

export const witchHooks: {
  /** Throw a splash potion with an explicit velocity (blocks/tick). */
  throwPotion?: (x: number, y: number, z: number, vx: number, vy: number, vz: number, item: number, dmg: number) => void;
  /** Does the local player currently have effect `id`? */
  playerHasEffect?: (id: number) => boolean;
} = {};

const roll = (rnd: () => number, lo: number, hi: number): number => lo + Math.floor(rnd() * (hi - lo + 1));

export type WitchPotion = 'slowness' | 'poison' | 'weakness' | 'harming';

/** Vanilla splash potion pick for a throw. `has` says which effects the target already has. */
export function pickThrowPotion(dist: number, targetHealth: number, has: (id: number) => boolean, rnd: () => number): WitchPotion {
  if (dist >= 8 && !has(FX.SLOWNESS)) return 'slowness';
  if (targetHealth >= 8 && !has(FX.POISON)) return 'poison';
  if (dist <= 3 && !has(FX.WEAKNESS) && rnd() < 0.25) return 'weakness';
  return 'harming';
}

export class Witch extends Hostile {
  /** Ticks left drinking (0 = not). */
  drinking = 0;
  private drinkKey = '';
  private throwCd = 60;

  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'witch', x, y, z, { width: 0.6, height: 1.95, eye: 1.62, health: 26, speed: 0.25 }, rnd);
  }

  override xpValue(): number { return 5; }

  /** Which potion (key) the witch wants to drink now, or ''. */
  private wantDrink(ctx: MobCtx): string {
    const fx = this.effects;
    const r = ctx.host.rnd;
    if (this.inWater && !fx?.has(FX.WATER_BREATHING)) return 'water_breathing';
    if (this.fireTicks > 0 && !fx?.has(FX.FIRE_RESISTANCE) && r() < 0.15) return 'fire_resistance';
    if (this.health < this.maxHealth && r() < 0.05) return 'healing';
    if (this.hasTarget && !fx?.has(FX.SPEED) && r() < 0.5) {
      const p = ctx.host.player;
      if ((p.x - this.x) ** 2 + (p.z - this.z) ** 2 > 100) return 'swiftness';
    }
    return '';
  }

  protected ai(ctx: MobCtx): void {
    if (this.throwCd > 0) this.throwCd--;
    const has = this.acquire(ctx, 16);
    const p = ctx.host.player;
    if (this.drinking > 0) {
      // 32-tick potion use; effect applies at the end
      if (--this.drinking === 0) this.finishDrink();
      return;
    }
    const key = this.wantDrink(ctx);
    if (key) {
      this.drinkKey = key;
      this.drinking = 32;
      this.heldItem = I.POTION;
      return;
    }
    if (!has) {
      this.stroll(ctx, 0.8, 4, false);
      this.lookAtNearPlayer(ctx);
      return;
    }
    this.aggressive = true;
    this.faceToward(p.x, p.z);
    this.lookAt(p.x, p.eyeY, p.z);
    const dx = p.x - this.x, dz = p.z - this.z;
    const d = Math.hypot(dx, dz);
    // vanilla RangedAttackGoal(1.0, 60, 10): close in until within 10 and in sight, then stand and throw
    if (d > 10 || !this.sees) this.chase(ctx, 1);
    if (this.sees && d <= 10 && this.throwCd <= 0) this.throwAt(ctx, d);
  }

  private finishDrink(): void {
    const k = this.drinkKey;
    this.heldItem = 0;
    this.drinkKey = '';
    if (!k) return;
    for (const e of potionByKey(k).effects) {
      if (e.id === FX.INSTANT_HEALTH) this.health = Math.min(this.maxHealth, this.health + 4 * (e.amp + 1));
      else giveMobEffect(this, e.id, e.amp, e.dur);
    }
  }

  private throwAt(ctx: MobCtx, d: number): void {
    const p = ctx.host.player;
    const has = (id: number): boolean => witchHooks.playerHasEffect?.(id) ?? false;
    const key = pickThrowPotion(d, 20, has, ctx.host.rnd);
    const sx = this.x, sy = this.eyeY - 0.1, sz = this.z;
    const dx = p.x - sx, dy = p.y + p.height * 0.5 - sy, dz = p.z - sz;
    // vanilla: shoot(dx, dy + h * 0.2, dz, 0.75, 8)
    const h = Math.hypot(dx, dz) || 1;
    const ty = dy + h * 0.2;
    const l = Math.hypot(dx, ty, dz) || 1;
    witchHooks.throwPotion?.(sx, sy, sz, (dx / l) * 0.75, (ty / l) * 0.75, (dz / l) * 0.75, I.SPLASH_POTION, potionByKey(key).index);
    this.throwCd = 60;
    this.swing();
  }

  loot(rnd: () => number): Stack[] {
    const out: Stack[] = [];
    for (const id of [I.GLASS_BOTTLE, I.GLOWSTONE_DUST, I.GUNPOWDER, I.REDSTONE, I.SPIDER_EYE, I.SUGAR, I.STICK]) {
      const n = roll(rnd, 0, 2);
      if (n > 0) out.push(stackOf(id, n));
    }
    return out;
  }
}
